import filter from "lodash/filter";
import findIndex from "lodash/findIndex";
import find from "lodash/find";
import each from "lodash/each";
import countBy from "lodash/countBy";
// Cherry-picked lodash methods kept under `_` so existing call sites are unchanged
const _ = { filter, findIndex, find, each, countBy };
import {$http} from '@/utils/https';
import type {ActionContext} from "vuex";
import type {RootState} from "@/store";
import type {BasketProduct, Product, ProductPrice} from "@/types/shop";
import type { OrderProduct } from "@/types/order";
import { makeid } from "@/helpers/helpers";
import { basketRowKind } from "@/helpers/basket-payload";

const saveBasketProducts = (products: Array<BasketProduct>): void => {
  const parsed = JSON.stringify(products);
  localStorage.setItem('products', parsed);
};

const loadBasketProducts = (): Array<BasketProduct> => {
  if (localStorage.getItem('products')) {
    const products = JSON.parse(localStorage.getItem('products') || '[]');

    return _.filter(products);
  }

  return [];
}

// state
interface State {
  buyLoading: boolean;
  promoCode: string | null;
  discount: any;
  loadingPromoCode: boolean;
  productsWithDefaultPrices: Array<Product> | null;
  basket: Array<BasketProduct>;
  defaultBasket: Array<BasketProduct>;
  isShowBasketPopup: boolean;
  buyTogetherProducts: Array<Product>;
  clientProducts: Array<Product>;
  selectedBonuses: selectedBonuses,
  promotionResponse: any,
  unavailableRemoved: Array<BasketProduct>
}

interface selectedBonuses {
  pay_bonus_amount: number
}

interface AddBasketPayload {
  select_count: number;
  product_price: ProductPrice;
  product: Product;
  discount?: number;
  isPresent: any;
  isDiscountedUnit?: boolean;
  isSamplerPresent?: any;
  isAddedFromSale: any;
  isFree?: boolean;
  saleId?: number | null;
  pricePer1: number;
  discountPer1: number;
  allProductSum: number;
}

interface SetCountPayload {
  product: BasketProduct;
  select_count: number;
}

interface HandlePayload {
  basketProduct: BasketProduct;
  product: Product;
  productPrice: ProductPrice;
}

interface FindProductData {
  basketProduct: BasketProduct;
  product: Product;
  productPrice: ProductPrice;
}


const state: State = {
  buyLoading: false,
  promoCode: '',
  discount: null,
  loadingPromoCode: false,
  productsWithDefaultPrices: null,
  basket: loadBasketProducts(),
  defaultBasket: [],
  isShowBasketPopup: false,
  buyTogetherProducts: [],
  clientProducts: [],
  promotionResponse: null,
  unavailableRemoved: [],
  selectedBonuses: {
    pay_bonus_amount: 0
  }
};

// Rows the backend created (promo gift / free sampler / sale gift) must never
// collapse into the customer's own row for the same product+price: a merged row
// loses the gift markers and gets sent back to sync-basket as a paid purchase.
const rowKindOf = (row: any): string => basketRowKind(row);

const buildMergeKey = (productId: number, productPriceId: number, kind: string): string =>
  `${productId}-${productPriceId}-${kind}`;

const hasProductTypeInBasket = (list: Array<BasketProduct>, type: string): boolean => {
  const counts = _.countBy(list, 'type');

  return (counts[type] || 0) > 0
}

// getters
const getters = {
  selectedBonuses: (state: State) => state.selectedBonuses,
  buyTogetherProducts: (state: State) => state.buyTogetherProducts,
  isShowBasketPopup: (state: State) => state.isShowBasketPopup,
  productsWithDefaultPrices: (state: State) => state.productsWithDefaultPrices,


  promoCode: (state: State) => state.promoCode,
  discount: (state: State) => state.discount,
  buyLoading: (state: State) => state.buyLoading,
  loadingPromoCode: (state: State) => state.loadingPromoCode,
  basket: (state: State) => state.basket || [],
  clientProducts: (state: State) => state.clientProducts || [],
  promotionResponse: (state: State) => state.promotionResponse,
  unavailableRemoved: (state: State) => state.unavailableRemoved || [],
};


// actions
const actions = {
  // CHECK_PROMO_CODE: async ({commit}: ActionContext<State, RootState) => {
  //   commit('CHECK_PROMO_CODE_LOADING', true);
  //   commit('RESET_DISCOUNT');
  //   try {
  //     const response = await $http.post(`v1/order/coupon`, {code: state.promoCode});
  //
  //     commit('SET_DISCOUNT', response.data.data.percent);
  //     return response.data;
  //   } catch (e) {
  //     throw e;
  //   } finally {
  //     commit('CHECK_PROMO_CODE_LOADING', false);
  //   }
  // },

  GET_BUY_TOGETHER_PRODUCT: async ({commit}: ActionContext<State, RootState>, payload: {
    ids: Array<number | string>
  }) => {
    // commit('CHECK_PROMO_CODE_LOADING', true);
    try {
      const response = await $http.get('v1/basket/products/buy-with-in-basket', {
        params: {
          products: payload.ids,
        }
      });
      commit('SET_BUY_TOGETHER_PRODUCT', response.data.data);
      // return response.data;
    } catch (e) {
      throw e;
    } finally {
      // commit('CHECK_PROMO_CODE_LOADING', false);
    }
  },
  GET_SYNC_BASKET: async ({commit}: ActionContext<State, RootState>, payload: { ids: Array<number | string> }) => {
    commit('CHECK_PROMO_CODE_LOADING', true);
    try {
      const response = await $http.get('v1/basket/products', {
        params: {
          ids: payload.ids
        }
      });
      commit('SET_BASKET_PRODUCTS', response.data.data);
      return response.data;
    } catch (e) {
      throw e;
    } finally {
      commit('CHECK_PROMO_CODE_LOADING', false);
    }
  },
  GET_DEFAULT_PRICE_BASKET: async ({commit}: ActionContext<State, RootState>, payload: {
    ids: Array<number | string>
  }) => {
    commit('CHECK_PROMO_CODE_LOADING', true);
    try {
      const response = await $http.get('v1/basket/products', {
        params: {
          ids: payload.ids,
          default_price: 1
        }
      });
      commit('SET_PRODUCTS_WITH_DEFAULT_PRICES', response.data.data);
      return response.data;
    } catch (e) {
      console.error(e)
    } finally {
      commit('CHECK_PROMO_CODE_LOADING', false);
    }
  },
  FETCH_PROMOCODE_INFO: async ({commit}: ActionContext<State, RootState>, payload: any) => {
    try {
      type TFetchResponse = { products: Array<any>, promocode: { value: string | null, activated: boolean } };

      const response = await $http.post<TFetchResponse>('/v1/checkout/sync-basket', payload);
      const mergedProducts: { [key: string]: BasketProduct } = {};

      // @ts-ignore
      response.data.order.orderProducts.data.forEach((orderData: OrderProduct) => {
        const foundProduct = response.data.products.find((product: any) => product.productPrice.id === orderData.productPriceId);
        if (!foundProduct) return;

        // @ts-ignore
        const odIsPresent = orderData.isPresent;
        const productKey = buildMergeKey(foundProduct.product.id, foundProduct.productPrice.id, rowKindOf(orderData));

        if (mergedProducts[productKey]) {
          mergedProducts[productKey].select_count += orderData.count;
          // @ts-ignore
          mergedProducts[productKey].discount += orderData.discount || 0;
          // @ts-ignore
          mergedProducts[productKey].allProductSum += orderData.sum || 0;
        } else {
          mergedProducts[productKey] = makeBasketProduct({
            product: foundProduct.product,
            product_price: foundProduct.productPrice,
            select_count: orderData.count,
            discount: orderData.discount || 0,
            isPresent: odIsPresent,
            // @ts-ignore
            isDiscountedUnit: orderData.isDiscountedUnit || false,
            isSamplerPresent: orderData.isSamplerPresent || false,
            isAddedFromSale: orderData.isAddedFromSale,
            // @ts-ignore
            isFree: orderData.isFree || false,
            // @ts-ignore
            saleId: orderData.saleId ?? null,
            pricePer1: orderData.pricePer1,
            discountPer1: orderData.discountPer1,
            // @ts-ignore
            allProductSum: orderData.sum
          });
        }
      });

      // @ts-ignore
      state.basket = Object.values(mergedProducts);
      saveBasketProducts(state.basket);

      return response.data;
    } catch (e) {
      throw e
    }
  },
  FETCH_PROMOTION: async ({ commit, dispatch }: ActionContext<State, RootState>, payload: any) => {
      console.log('FETCH_PROMOTION')
    if (!payload || !payload.products || !payload.products.length) {
      commit('SET_PROMOTION_RESPONSE', null);
      return null;
    }

    try {
      type TFetchResponse = { products: Array<BasketProduct & { productPrice: ProductPrice, product: Product }>, promocode: { value: string | null, activated: boolean } };

      const response = await $http.post<TFetchResponse>('/v1/checkout/sync-basket', payload);
      const mergedProducts: { [key: string]: BasketProduct } = {};

      commit('SET_PROMOTION_RESPONSE', response.data);

      // Update existing basket products with pricing from response
      // @ts-ignore
      response.data.order.orderProducts.data.forEach((orderData: OrderProduct) => {
        const foundProduct = response.data.products.find(product => product.productPrice.id === orderData.productPriceId);

        if (foundProduct) {
          // @ts-ignore
          const odIsPresent = orderData.isPresent;
          const productKey = buildMergeKey(foundProduct.product.id, foundProduct.productPrice.id, rowKindOf(orderData));

          if (mergedProducts[productKey]) {
            mergedProducts[productKey].select_count += orderData.count;
            // @ts-ignore
            mergedProducts[productKey].discount += orderData.discount || 0;
            // @ts-ignore
            mergedProducts[productKey].allProductSum += orderData.sum || 0;
          } else {
            mergedProducts[productKey] = makeBasketProduct({
              product: foundProduct.product,
              product_price: foundProduct.productPrice,
              select_count: orderData.count,
              discount: orderData.discount || 0,
              isPresent: odIsPresent,
              // @ts-ignore
              isDiscountedUnit: orderData.isDiscountedUnit || false,
              isSamplerPresent: orderData.isSamplerPresent || false,
              isAddedFromSale: orderData.isAddedFromSale,
              // @ts-ignore
              isFree: orderData.isFree || false,
              // @ts-ignore
              saleId: orderData.saleId ?? null,
              pricePer1: orderData.pricePer1,
              discountPer1: orderData.discountPer1,
              // @ts-ignore
              allProductSum: orderData.sum
            });
          }
        } else {
          // Product not in response.products — update pricing on existing basket item
          const orderProductData = orderData.product?.data;
          if (!orderProductData) return;

          // @ts-ignore
          const odIsPresent = orderData.isPresent;
          const odKind = rowKindOf(orderData);
          const basketItem = state.basket.find(
            (item: BasketProduct) => item.id === orderProductData.id && rowKindOf(item) === odKind
          );
          if (basketItem) {
            // @ts-ignore
            basketItem.discount = orderData.discount || 0;
            // @ts-ignore
            basketItem.pricePer1 = orderData.pricePer1;
            // @ts-ignore
            basketItem.discountPer1 = orderData.discountPer1;
            // @ts-ignore
            basketItem.allProductSum = orderData.sum;
            basketItem.isPresent = odIsPresent;
            // @ts-ignore
            basketItem.isDiscountedUnit = orderData.isDiscountedUnit || false;
            // @ts-ignore
            basketItem.isSamplerPresent = orderData.isSamplerPresent || false;
            // @ts-ignore
            basketItem.isAddedFromSale = orderData.isAddedFromSale || false;
            // @ts-ignore
            basketItem.isFree = orderData.isFree || false;
            // @ts-ignore
            basketItem.saleId = orderData.saleId ?? null;
            // Update currency and price from order data
            if (orderData.currency && basketItem.select_type) {
              basketItem.select_type.currency = orderData.currency;
              basketItem.select_type.price = orderData.pricePer1;
              basketItem.select_type.basePrice = orderData.pricePer1 + (orderData.discountPer1 || 0);
              // The order response carries no 30-day low, so the one sitting here belongs to the
              // price that was just replaced. Keeping it would pair a fresh price with a stale
              // floor - a §12a claim about a discount nobody computed. Dropped rather than
              // guessed: showsLowestPrice() reads a missing field as "no data" and renders
              // nothing, which is the honest state. Also covers rows restored from localStorage
              // that predate these fields.
              // @ts-ignore
              basketItem.select_type.lowestPrice30D = null;
              // @ts-ignore
              basketItem.select_type.priceHistorySince = null;
              // @ts-ignore
              basketItem.select_type.hasFullPriceHistory = false;
            }
            if (orderData.currency && basketItem.productPrices?.data?.[0]) {
              // @ts-ignore
              basketItem.productPrices.data[0].currency = orderData.currency;
            }
            mergedProducts[buildMergeKey(basketItem.id, basketItem.select_type.id, odKind)] = basketItem;
          } else if (odKind !== 'main') {
            // New gift item returned by backend — merge into basket from nested product data
            const nested: any = orderProductData;
            const nestedPriceList = nested.productPrices?.data || [];
            const nestedProductPrice =
              nestedPriceList.find((p: any) => p.id === orderData.productPriceId) ||
              nestedPriceList[0];
            if (nestedProductPrice) {
              const productForBuild: any = nested.product
                ? { ...nested.product }
                : { ...nested };
              if (!productForBuild.productPrices) {
                productForBuild.productPrices = nested.productPrices;
              }
              if (!productForBuild.image && nested.image) {
                productForBuild.image = nested.image;
              }
              if (!productForBuild.title && nested.title) {
                productForBuild.title = nested.title;
              }
              if (!productForBuild.slug && nested.slug) {
                productForBuild.slug = nested.slug;
              }
              // @ts-ignore
              const odIsAddedFromSale = orderData.isAddedFromSale;
              mergedProducts[buildMergeKey(productForBuild.id, nestedProductPrice.id, odKind)] =
                makeBasketProduct({
                  product: productForBuild,
                  product_price: nestedProductPrice,
                  select_count: orderData.count,
                  discount: orderData.discount || 0,
                  isPresent: odIsPresent,
                  // @ts-ignore
                  isDiscountedUnit: orderData.isDiscountedUnit || false,
                  // @ts-ignore
                  isSamplerPresent: orderData.isSamplerPresent || false,
                  isAddedFromSale: odIsAddedFromSale,
                  // @ts-ignore
                  isFree: orderData.isFree || false,
                  // @ts-ignore
                  saleId: orderData.saleId ?? null,
                  // @ts-ignore
                  pricePer1: orderData.pricePer1,
                  // @ts-ignore
                  discountPer1: orderData.discountPer1,
                  // @ts-ignore
                  allProductSum: orderData.sum,
                });
            }
          }
        }
      });

      // @ts-ignore
      state.basket = Object.values(mergedProducts);
      saveBasketProducts(state.basket);

      return response.data;
    } catch (e) {
        console.error(e)
        // @ts-ignore
        if (e && e.status === 422) throw e

        // A `400` here is how the backend reports a row it cannot resolve at all -
        // typically a price removed since the basket was filled. Retrying the same
        // payload can only fail the same way, so drop the dead rows first and sync
        // once more with what is left. `retried` stops a backend fault that answers
        // `400` for a healthy basket from looping.
        // @ts-ignore
        if (e && e.status === 400 && !payload.retried) {
          try {
            const dropped = await dispatch('DROP_UNAVAILABLE_PRODUCTS');

            if (dropped.removed.length) {
              commit('SET_UNAVAILABLE_REMOVED', dropped.removed);

              const surviving = _.filter(
                payload.products,
                (product: any) => dropped.priceIds.indexOf(product.product_price_id) !== -1
              );

              if (!surviving.length) {
                commit('SET_PROMOTION_RESPONSE', null);
                return null;
              }

              return await dispatch('FETCH_PROMOTION', {...payload, products: surviving, retried: true});
            }
          } catch (dropError) {
            console.error(dropError)
          }
        }

        return null
    }
  },
  /**
   * Drop basket rows whose price no longer exists on the backend.
   *
   * The basket lives in localStorage and outlives deploys, so a row can keep
   * pointing at a `product_price_id` that has since been removed. sync-basket
   * answers such a row with a bare `400` and a generic message - no field, no id -
   * so the whole basket stops syncing: stale prices, no promotion recalculation,
   * and the customer has no way out short of clearing site data.
   *
   * `v1/basket/products` is the only endpoint that can say which row went missing,
   * and unlike a second sync-basket call it creates no draft order. A product that
   * is gone is simply absent from the response; a product that survived but lost
   * one of its volumes still lists the prices it has left, so both cases are
   * covered by checking the price id against what came back.
   *
   * Returns the price ids that are still valid, so the caller can retry with the
   * rows that survived instead of rebuilding the payload.
   */
  DROP_UNAVAILABLE_PRODUCTS: async ({commit, state}: ActionContext<State, RootState>) => {
    const productIds: Array<number> = [];
    _.each(state.basket, (item: BasketProduct) => {
      if (item && item.id && productIds.indexOf(item.id) === -1) productIds.push(item.id);
    });

    if (!productIds.length) return {removed: [], priceIds: []};

    const response = await $http.get('v1/basket/products', {params: {ids: productIds}});
    const products = (response.data && response.data.data) || [];

    // A malformed answer must never be read as "every product is gone" - that
    // would wipe a basket over a transient backend fault.
    if (!Array.isArray(products)) return {removed: [], priceIds: []};

    const priceIds: Array<number> = [];
    _.each(products, (product: any) => {
      const prices = (product && product.productPrices && product.productPrices.data) || [];
      _.each(prices, (price: any) => {
        if (price && price.id && priceIds.indexOf(price.id) === -1) priceIds.push(price.id);
      });
    });

    const removed = _.filter(state.basket, (item: BasketProduct) => (
      !item.select_type || priceIds.indexOf(item.select_type.id) === -1
    ));

    if (removed.length) commit('REMOVE_UNAVAILABLE_ITEMS', priceIds);

    return {removed, priceIds};
  },
  GET_CLIENT_PRICE_BASKET: async ({commit}: ActionContext<State, RootState>, payload: {
    ids: Array<number | string>
  }) => {
    try {
      const response = await $http.get('v1/basket/products', {
        params: {
          ids: payload.ids,
          client_price: 1
        }
      });
      commit('SET_PRODUCTS_WITH_CLIENT_PRICES', response.data.data);
      return response.data;
    } catch (e) {
      throw e;
    } finally {
    }
  },
  GET_CLIENT_PRICE_BASKET_FOR_MODAL: async ({commit}: ActionContext<State, RootState>, payload: {
    ids: Array<number | string>
  }) => {
    try {
      const response = await $http.get('v1/basket/products', {
        params: {
          ids: payload.ids,
          client_price: 1
        }
      });
      commit('SET_PRODUCTS_WITH_CLIENT_PRICES_FOR_MODAL', response.data.data);
      return response.data;
    } catch (e) {
      throw e;
    } finally {
    }
  },
};


const reloadBasketProducts = (state: State) => {
  const newBasket = loadBasketProducts();

  if (newBasket.length !== state.basket.length) {
    state.basket = newBasket
  }
}

const findProductIndex = (list: Array<BasketProduct>, basketProduct: BasketProduct): number => {
  const foundHash =  _.findIndex(list, (product) => {
        return product.hash === basketProduct.hash
      },
  );
  if (foundHash !== -1) return foundHash
  const kind = rowKindOf(basketProduct);
  return _.findIndex(list, (product) =>
      product.id === basketProduct.id
      && product.select_type.id === basketProduct.select_type.id
      && rowKindOf(product) === kind
  );
};

const replaceProduct = (basketProduct: BasketProduct): Array<BasketProduct> => {
  const basketProducts = state.basket;

  const index = findProductIndex(basketProducts, basketProduct);
  if (index !== -1) {
    basketProducts[index] = basketProduct;
  } else {
    basketProducts.push(basketProduct)
  }

  return basketProducts;
}

const resolveProductCount = (basketProduct: BasketProduct, count: number | any): BasketProduct => {
  basketProduct.select_count = 0;

  if (!basketProduct.select_type.quantity) {
    basketProduct.select_count = count;
    return basketProduct;
  }

  const setQuantity = Math.min(
    Math.max(Number.parseFloat(count || '1'), 1),
    basketProduct.select_type.quantity
  );

  basketProduct.select_count = Math.ceil(setQuantity)

  return basketProduct;
}

const updateCountForProduct = (payload: SetCountPayload): Array<BasketProduct> => {
  const BasketProductsList = state.basket;

  console.log('change-count', payload.product.hash);

  const index = findProductIndex(BasketProductsList, payload.product);
  if (index !== -1) {
    BasketProductsList[index] = resolveProductCount(BasketProductsList[index], payload.select_count);
  }

  return BasketProductsList;
}

const makeBasketProduct = (payload: AddBasketPayload): BasketProduct => {
  const value = resolveProductCount({
    id: payload.product.id,
    title: payload.product.title,
    slug: payload.product.slug,
    image: payload.product.image,
    productPrices: payload.product.productPrices,
    type: payload.product.type,
    brand: payload.product.brand,
    brandSeries: payload.product.brandSeries,
    discount: payload.discount,

    select_count: payload.select_count || 1,
    select_type: payload.product_price,
    client_price: undefined,
    client_with_code_price: undefined,
    isPresent: payload.isPresent,
    isDiscountedUnit: payload.isDiscountedUnit || false,
    isSamplerPresent: payload.isSamplerPresent || false,
    isAddedFromSale : payload.isAddedFromSale,
    isFree: payload.isFree || false,
    saleId: payload.saleId ?? null,
    hash: makeid(12),
    // @ts-ignore
    pricePer1: payload.pricePer1,
    // @ts-ignore
    discountPer1: payload.discountPer1,
    // @ts-ignore
    allProductSum: payload.allProductSum
  }, payload.select_count)
  return value;
}

const updateBasketProduct = (basketProduct: BasketProduct, product: Product): BasketProduct => {
  // @ts-ignore
  return resolveProductCount({
    id: product.id,
    title: product.title,
    slug: product.slug,
    image: product.image,
    productPrices: product.productPrices,
    type: product.type,
    brand: product.brand,
    brandSeries: product.brandSeries,

    select_count: basketProduct.select_count,
    select_type: basketProduct.select_type,
    client_price: basketProduct.client_price,
    client_with_code_price: basketProduct.client_with_code_price,
    isPresent: basketProduct.isPresent,
    // @ts-ignore
    isDiscountedUnit: basketProduct.isDiscountedUnit || false,
    isSamplerPresent: basketProduct.isSamplerPresent || false,
    isAddedFromSale : basketProduct.isAddedFromSale,
    isFree: basketProduct.isFree || false,
    saleId: basketProduct.saleId ?? null,
    // @ts-ignore
    pricePer1: basketProduct.pricePer1,
    // @ts-ignore
    discountPer1: basketProduct.discountPer1,
    // @ts-ignore
    discount: basketProduct.discount,
    // @ts-ignore
    allProductSum: basketProduct.allProductSum
  }, basketProduct.select_count)
}

const findProduct = (products: Array<Product>, basketProduct: BasketProduct): FindProductData | null => {
  const product = _.find(products, ({id}) => basketProduct.id === id)

  if (!product) {
    return null;
  }

  const productPrice = _.find(product.productPrices.data, ({value}) => basketProduct.select_type.value === value)

  if (!productPrice) {
    return null;
  }

  return {basketProduct, product, productPrice}
}

const refreshBasketProducts = (basketProducts: Array<BasketProduct>, products: Array<Product>, itemHandler: (payload: HandlePayload) => BasketProduct) => {
  const basket: Array<BasketProduct> = [];

  _.each(basketProducts, (basketProduct: BasketProduct): void => {
    const productData = findProduct(products, basketProduct)

    if (!productData) {
      basket.push(basketProduct)
      return
    }

    basket.push(updateBasketProduct(itemHandler(productData), productData.product))
  });

  return basket;
};

const addProductToBasket = (state: State, payload: AddBasketPayload): Array<BasketProduct> => {
  if (payload.product.hasDuplicate) {
    return state.basket
  }

  let basketProduct = state.basket.find(basketProduct => basketProduct.id === payload.product.id
    && basketProduct.select_type.value === payload.product_price.value);

  if (basketProduct) {
    payload.select_count++;
  } else {
    basketProduct = makeBasketProduct(payload)
  }

  return replaceProduct(basketProduct);
}

setInterval(() => {
  reloadBasketProducts(state)
}, 10000);

// mutations
const mutations = {
  ADD_ITEM_TO_BASKET(state: State, payload: AddBasketPayload) {
    state.basket = addProductToBasket(state, payload)

    saveBasketProducts(state.basket);
  },
  ADD_WEBINAR_ITEM_TO_BASKET(state: State, payload: AddBasketPayload) {
    state.basket = addProductToBasket(state, payload)

    saveBasketProducts(state.basket);
  },
  REMOVE_ITEM_FROM_BASKET(state: State, basketProduct: BasketProduct) {
    const foundProduct = state.basket.find(product => product.id === basketProduct.id)
    if (foundProduct) {
      // @ts-ignore
      window.dataLayer.push({ ecommerce: null });
      // @ts-ignore
      window.dataLayer.push({
        event: "remove_from_cart",
        ecommerce: {
          // @ts-ignore
          currency: foundProduct.select_type.currency || "EUR",
          value: foundProduct.select_type.price,
          items: [
            {
              item_id: foundProduct.select_type.id,
              item_name: foundProduct.title,
              affiliation: "Renew-Cosmetics",
              coupon: "",
              discount:  0.00,
              index: 0,
              item_brand: foundProduct?.brand?.data?.title || 'Renew',
              item_category: foundProduct?.brandSeries?.data?.title || '',
              item_list_id: "Cosmetics",
              item_list_name: "Cosmetics",
              location_id: "ChIJIQBpAG2ahYAR_6128GcTUEo",
              price: foundProduct.select_type.price,
              quantity: 1
            }
          ]
        }
      });
    }
    const index = findProductIndex(state.basket, basketProduct);
    // Vue.delete with -1 splices the last row off, so a row that is already gone
    // would take an unrelated product with it.
    if (index === -1) return;

    const target = state.basket[index];
    const targetKind = rowKindOf(target);

    // Removing a position takes its promotion rows with it: the 0.01 unit a
    // "third free" promotion split off is one of the customer's own units, and
    // leaving it behind keeps the product in the basket after it was removed —
    // the next sync counts it and re-applies the promotion.
    const removesWholePosition = targetKind === 'main' || targetKind === 'discounted';

    state.basket = _.filter(state.basket, (item: BasketProduct) => {
      if (item === target) return false;
      if (!removesWholePosition) return true;

      return !(
        item.id === target.id
        && item.select_type.id === target.select_type.id
        && !item.isFree
        && ['main', 'discounted', 'present'].indexOf(rowKindOf(item)) !== -1
      );
    });

    saveBasketProducts(state.basket);
  },
  /**
   * Keep only the rows whose price the backend still knows about. Gift and
   * sampler rows the backend adds itself are dropped along with everything else -
   * the next sync recreates them from the rows that survived.
   */
  REMOVE_UNAVAILABLE_ITEMS(state: State, priceIds: Array<number>) {
    state.basket = _.filter(state.basket, (item: BasketProduct) => (
      !!item.select_type && priceIds.indexOf(item.select_type.id) !== -1
    ));

    saveBasketProducts(state.basket);
  },
  SET_UNAVAILABLE_REMOVED(state: State, items: Array<BasketProduct>) {
    state.unavailableRemoved = items || [];
  },
  RESET_BASKET(state: State) {
    state.basket = [];

    saveBasketProducts(state.basket)
  },
  /**
   * Set the quantity of a whole position, however many rows the backend split it
   * into. A promotion can spread the customer's units over a paid row and a 0.01
   * row (`is_discounted_unit`), so the position collapses back to one plain row
   * carrying the new count — the promotion rows are recreated by the next sync
   * from the quantity we send. Collapsing in one step also covers the case where
   * the promotion discounted the entire position and no paid row is left to
   * carry the count.
   */
  SET_POSITION_COUNT(state: State, countPayload: SetCountPayload) {
    const target = countPayload.product;
    const isSamePosition = (item: BasketProduct): boolean => (
      item.id === target.id
      && item.select_type.id === target.select_type.id
      && !item.isFree
      && !item.isSamplerPresent
      && !item.isAddedFromSale
    );

    const ownRows = _.filter(state.basket, (item: BasketProduct) =>
      isSamePosition(item) && ['main', 'discounted'].indexOf(rowKindOf(item)) !== -1
    );
    const keep = _.find(ownRows, (item: BasketProduct) => rowKindOf(item) === 'main') || ownRows[0];

    if (!keep) return;

    keep.isPresent = false;
    // @ts-ignore
    keep.isDiscountedUnit = false;

    state.basket = _.filter(state.basket, (item: BasketProduct) => item === keep || !isSamePosition(item));
    state.basket = updateCountForProduct({ product: keep, select_count: countPayload.select_count });

    saveBasketProducts(state.basket);
  },
  CHECK_PROMO_CODE_LOADING(state: State, status: boolean) {
    state.loadingPromoCode = status;
  },
  SET_PROMO_CODE_IN_BASKET(state: State, code: string | null) {
    state.promoCode = code;
  },
  SET_DISCOUNT(state: State, discount: any) {
    state.discount = discount;
  },
  RESET_DISCOUNT(state: State) {
    state.discount = null;
  },
  SET_BASKET_PRODUCTS(state: State, products: Array<Product>) {
    const basket: Array<BasketProduct> = []

    each(state.basket, (basketProduct: BasketProduct): void => {
      // Gifts / samples / sale items keep their price id untouched: it is tied to
      // a sale on the backend and must not be re-bound to the auth user's price
      // type. (isFree/isPresent/isSamplerPresent/isAddedFromSale/saleId are the
      // gift markers.)
      const isSpecial = !!(
        rowKindOf(basketProduct) !== 'main' ||
        basketProduct.saleId != null
      )

      const productData = isSpecial ? null : findProduct(products, basketProduct)

      if (!productData) {
        // Either a special item we deliberately skip, or a product the sync
        // didn't return — keep it as-is so nothing silently disappears or loses
        // its markers.
        basket.push(basketProduct)
        return
      }

      // Re-price a regular product to the authenticated user's price type while
      // carrying over its markers, identity (hash) and client-price columns.
      // @ts-ignore
      const repriced = makeBasketProduct({
        product: productData.product,
        product_price: productData.productPrice,
        select_count: basketProduct.select_count || 1,
        // @ts-ignore
        discount: basketProduct.discount,
        isPresent: basketProduct.isPresent,
        // @ts-ignore
        isDiscountedUnit: basketProduct.isDiscountedUnit || false,
        isSamplerPresent: basketProduct.isSamplerPresent || false,
        isAddedFromSale: basketProduct.isAddedFromSale,
        isFree: basketProduct.isFree || false,
        saleId: basketProduct.saleId ?? null,
        // @ts-ignore
        pricePer1: basketProduct.pricePer1,
        // @ts-ignore
        discountPer1: basketProduct.discountPer1,
        // @ts-ignore
        allProductSum: basketProduct.allProductSum,
      })

      repriced.hash = basketProduct.hash
      repriced.client_price = basketProduct.client_price
      repriced.client_with_code_price = basketProduct.client_with_code_price

      basket.push(repriced)
    })

    state.basket = basket;
    saveBasketProducts(state.basket)
  },
  SET_PRODUCTS_WITH_DEFAULT_PRICES(state: State, products: Array<Product>) {
    state.productsWithDefaultPrices = products

    const itemHandler = ({basketProduct, productPrice}: HandlePayload): BasketProduct => {
      basketProduct.client_price = productPrice

      return basketProduct
    }

    state.basket = refreshBasketProducts(state.basket, products, itemHandler)
    saveBasketProducts(state.basket)
  },
  SET_PRODUCTS_WITH_CLIENT_PRICES(state: State, products: Array<Product>) {
    state.productsWithDefaultPrices = products

    const itemHandler = ({basketProduct, productPrice}: HandlePayload): BasketProduct => {
      basketProduct.client_with_code_price = productPrice

      return basketProduct
    }

    state.basket = refreshBasketProducts(state.basket, products, itemHandler)
    saveBasketProducts(state.basket)
  },
  SET_PRODUCTS_WITH_CLIENT_PRICES_FOR_MODAL(state: State, products: Array<Product>) {
    state.productsWithDefaultPrices = products
  },
  SET_BUY_TOGETHER_PRODUCT(state: State, data: Array<Product>) {
    state.buyTogetherProducts = data;
  },
  CLEAR_BYTOGETHER_PRODUCTS(state: State) {
    state.buyTogetherProducts = []
  },
  SET_USER_BONUSES(state: State, data: selectedBonuses) {
    state.selectedBonuses = data
  },
  SET_PROMOTION_RESPONSE(state: State, payload: any) {
    state.promotionResponse = payload
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
